import { useQuery } from '@tanstack/react-query';
import { getCreditBalanceCreditsBalanceGetOptions } from '@/client/@tanstack/react-query.gen';

/**
 * Hook for managing user's property credit balance
 * Provides real-time credit information and validation for bundle purchases
 * Always fetches fresh data to ensure accurate credit balance after payments
 */
export const useCreditBalance = () => {
	const {
		data: creditBalance,
		isLoading,
		error,
	} = useQuery({
		...getCreditBalanceCreditsBalanceGetOptions(),
		// Disable caching to always fetch fresh credit data
		staleTime: 0, // Data is immediately stale
		gcTime: 0, // Don't cache data in memory
		refetchOnMount: 'always', // Always refetch when component mounts
		refetchOnWindowFocus: true, // Refetch when window regains focus
		refetchOnReconnect: true, // Refetch when network reconnects
	});

	const hasCreditsForBundle = (requiredCredits: number) => {
		if (!creditBalance) return false;
		return creditBalance.available_credits >= requiredCredits;
	};

	return {
		creditBalance,
		isLoading,
		error,
		hasCreditsForBundle,
		availableCredits: creditBalance?.available_credits ?? 0,
		totalCredits: creditBalance?.total_credits ?? 0,
		usedCredits: creditBalance?.used_credits ?? 0,
	};
};
